import { type ChangeEvent, type Ref, useState } from 'react';

import {
  Box,
  Card,
  Divider,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Legend,
  Paragraph,
  Text,
} from '@zillow/constellation-10';

import {
  type GrmCalculation,
  type GrmField,
  type GrmValues,
  formatCurrency,
  formatGrm,
  formatPercent,
  formatPercentRoundedDown,
} from './grm';
import {
  cardContentStyles,
  cardStyles,
  inputFieldStyles,
  inputGridStyles,
  resultListStyles,
  resultRowStyles,
  subtleTextStyles,
} from './styles';
import WholeNumberInput from './WholeNumberInput';

export type PropertyState = { name: string; values: GrmValues };

type PropertyCardProps = {
  defaultName: string;
  property: PropertyState;
  calculation: GrmCalculation;
  onChange: (property: PropertyState) => void;
  nameInputRef?: Ref<HTMLInputElement>;
  showHeading?: boolean;
};

const FIELDS: Array<{ field: GrmField; label: string; isCurrency: boolean }> = [
  { field: 'price', label: 'Property price or market value', isCurrency: true },
  { field: 'rentPerUnit', label: 'Monthly rent per unit', isCurrency: true },
  { field: 'units', label: 'Number of units', isCurrency: false },
  { field: 'otherIncome', label: 'Other monthly income', isCurrency: true },
];

export const getDisplayName = (property: PropertyState, defaultName: string) => property.name.trim() || defaultName;

const PropertyCard = ({
  defaultName,
  property,
  calculation,
  onChange,
  nameInputRef,
  showHeading = true,
}: PropertyCardProps) => {
  const displayName = getDisplayName(property, defaultName);
  const [touchedFields, setTouchedFields] = useState<Partial<Record<GrmField, boolean>>>({});
  const errors = calculation.ok ? {} : calculation.errors;

  const handleValueChange = (field: GrmField) => (value: string) => {
    onChange({ ...property, values: { ...property.values, [field]: value } });
  };

  const result = calculation.ok ? calculation.result : null;
  const resultRows = [
    { label: 'Monthly gross income', value: result && formatCurrency(result.monthlyGrossIncome) },
    { label: 'Annual gross income', value: result && formatCurrency(result.annualGrossIncome) },
    { label: 'Gross yield', value: result && formatPercent(result.grossYield) },
  ];

  return (
    <Card tone="neutral" outlined elevated={false} css={cardStyles}>
      <Box css={cardContentStyles}>
        {showHeading && (
          <Heading level={3} textStyle="heading-sm">
            {displayName}
          </Heading>
        )}

        <FormField
          optional
          label={<Label>Property name</Label>}
          control={
            <Input
              ref={nameInputRef}
              placeholder={defaultName}
              value={property.name}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onChange({ ...property, name: event.target.value })}
            />
          }
        />

        <FieldSet legend={<Legend>{`${displayName} inputs`}</Legend>}>
          <Box css={inputGridStyles}>
            {FIELDS.map(({ field, label, isCurrency }) => {
              const error = touchedFields[field] ? errors[field] : undefined;

              return (
                <FormField
                  key={field}
                  css={inputFieldStyles}
                  error={Boolean(error)}
                  label={<Label>{label}</Label>}
                  control={
                    <WholeNumberInput
                      value={property.values[field]}
                      onChange={handleValueChange(field)}
                      onBlur={() => setTouchedFields((touched) => ({ ...touched, [field]: true }))}
                      isCurrency={isCurrency}
                    />
                  }
                  description={error ? <FormHelp>{error}</FormHelp> : undefined}
                />
              );
            })}
          </Box>
        </FieldSet>

        <Divider />

        <Heading level={showHeading ? 4 : 3} textStyle="body-lg-bold">
          {`${displayName} results`}
        </Heading>

        <dl className={resultListStyles}>
          <dt>
            <Text textStyle="body">Gross rent multiplier</Text>
          </dt>
          <dd>
            <Text textStyle="heading-sm">{result ? `${formatGrm(result.grm)} years` : '—'}</Text>
          </dd>
          {resultRows.map(({ label, value }) => (
            <div key={label} className={resultRowStyles}>
              <dt>
                <Text textStyle="body">{label}</Text>
              </dt>
              <dd>
                <Text textStyle="body-bold">{value ?? '—'}</Text>
              </dd>
            </div>
          ))}
        </dl>

        {result && (
          <Paragraph css={subtleTextStyles}>
            {`Monthly gross income is ${formatPercentRoundedDown(result.monthlyIncomeToPrice)} of price and ${
              result.meetsOnePercentRule ? 'meets' : 'is below'
            } the 1% rule. The 1% rule checks whether monthly gross income is at least 1% of the purchase price.`}
          </Paragraph>
        )}
      </Box>
    </Card>
  );
};

export default PropertyCard;
